import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import LoadingState from '../common/LoadingState';
import { fmtDateTime, fmtDuration, errMsg, cachedGet } from './shared';

// One delivery: what went wrong and what to do, in plain words. The pipeline
// steps and raw errors stay one click away under Technical details.
const RunDetail = ({ runId }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [showTech, setShowTech] = useState(false);

    useEffect(() => {
        cachedGet(`/activity/runs/${runId}`).then((res) => setData(res.data)).catch((err) => setError(errMsg(err)));
    }, [runId]);

    if (error) return <div className="ex-inline-error">{error}</div>;
    if (!data) return <LoadingState label="Loading…" size="sm" />;

    const { run, explanation, scope, execution, execution_error } = data;
    const n = (v) => Number(v).toLocaleString('en-US');
    const copyLink = () => navigator.clipboard.writeText(`${window.location.origin}/activity/${encodeURIComponent(run.platform_key)}?run=${encodeURIComponent(run.id)}`);
    const failedStep = execution?.steps.find((s) => ['failed', 'timed out'].includes(s.status));

    return (
        <div className="act-run">
            {explanation ? (
                <div className="act-explain">
                    <div className="act-explain-head"><strong>{explanation.headline}</strong></div>
                    <div>{explanation.detail}</div>
                    {scope && (
                        <div className="act-scope">
                            {scope.still_unsaved > 0
                                ? `Still unsaved now: ${n(scope.still_unsaved)}${scope.unsaved_loan_ids.length < scope.still_unsaved ? ` (first ${scope.unsaved_loan_ids.length} shown)` : ''} - `
                                : 'All of them have been saved since, by a later delivery.'}
                            {scope.unsaved_loan_ids.length > 0 && <code>{scope.unsaved_loan_ids.join(', ')}</code>}
                        </div>
                    )}
                    {explanation.action && <div><strong>Next step:</strong> {explanation.action}</div>}
                </div>
            ) : (
                <div className="text-color-secondary">
                    {run.note || (run.state === 'stuck' ? 'No result after 3 hours - processing may have stopped.' : 'Nothing went wrong with this delivery.')}
                </div>
            )}

            <div className="act-facts-line text-color-secondary">
                {run.file_name || 'No file name'} · {run.channel}{run.uploaded_by ? ` by ${run.uploaded_by}` : ''} · {fmtDateTime(run.received_at)}
                {run.rows != null && ` · ${Number(run.rows).toLocaleString('en-US')} rows`}
            </div>

            <div className="ex-toolbar-group">
                <Button label={showTech ? 'Hide technical details' : 'Technical details'} icon="pi pi-code" className="p-button-sm p-button-text"
                    onClick={() => setShowTech(!showTech)} />
                <Button label="Copy link" icon="pi pi-link" className="p-button-sm p-button-text" onClick={copyLink} />
            </div>

            {showTech && (
                <div className="act-tech">
                    <dl className="ex-kv">
                        <dt>Request ID</dt><dd><code>{run.req_id}</code></dd>
                        <dt>Status</dt><dd>{run.status || '—'}</dd>
                        <dt>Data type</dt><dd>{run.data_type || '—'}</dd>
                    </dl>
                    {execution_error && <div className="text-color-secondary">Pipeline steps unavailable: {execution_error}</div>}
                    {execution && (
                        <>
                            <div className="act-subtitle">
                                Pipeline {execution.status.toLowerCase()}
                                {execution.stopped_at && `, took ${fmtDuration(new Date(execution.stopped_at) - new Date(execution.started_at))}`}
                                {failedStep && ` - failed at "${failedStep.name}"`}
                                {' · '}<a href={execution.console_url} target="_blank" rel="noreferrer">AWS console</a>
                            </div>
                            <ol className="act-steps">
                                {execution.steps.filter((s) => (s.type !== 'Pass' && s.type !== 'Choice') || s.status !== 'succeeded').map((s, i) => (
                                    <li key={i} className={`act-step act-step-${s.status.replace(' ', '-')}`}>
                                        <span className="act-step-name">{s.name}</span>
                                        <span className="act-step-meta">{s.status} · {fmtDuration(s.duration_ms)}</span>
                                    </li>
                                ))}
                            </ol>
                        </>
                    )}
                    {run.error_message && <><div className="act-subtitle">Error message</div><pre>{run.error_message}</pre></>}
                    {run.error_details && <><div className="act-subtitle">Error details</div><pre>{run.error_details}</pre></>}
                    {execution?.failure?.cause && <><div className="act-subtitle">Step error: {execution.failure.error}</div><pre>{execution.failure.cause}</pre></>}
                </div>
            )}
        </div>
    );
};

export default RunDetail;
